import { FEATURES } from "../config/features";

/**
 * Flow 1 — parent welcome (V0, level 1).
 * The texts are editable in the simulator's copy studio ({student} = the child,
 * {parent} = the parent). Steps 3 and 4 never promise a feature that is off.
 */

const child = (ctx) => ctx.child || "votre enfant";

function day(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const time = `${d.getHours()} h ${String(d.getMinutes()).padStart(2, "0")}`;
  return `${d.toLocaleDateString("fr-CA", { day: "numeric", month: "long" })} à ${time}`;
}

export const PARENT_STEP_TITLES = ["Accueil", "Votre enfant", "Premier engagement", "Suivi et rappels", "Accueil habituel"];

export const PARENT_V0_MESSAGES = [
  {
    key: "p1.welcome",
    flowId: 1,
    step: 0,
    label: "Accueil",
    prompt:
      "Accueille le parent par son prénom, dis que son compte est prêt, puis explique en deux phrases le principe : il propose de courtes périodes d'étude, l'enfant accepte, réalise et indique ce qu'il a fait. Vouvoiement, un emoji.",
    text: (ctx) =>
      ctx.count > 1
        ? `Bonjour ${ctx.parentName || "{parent}"} 👋\nVotre compte est prêt\u00a0!\n\nAvec Plan Étude, vous proposez de courtes périodes d’étude à vos enfants. Chacun accepte, réalise ses activités et indique ce qu’il a fait.\n\nVous suivez leur progression sans avoir à les relancer.`
        : `Bonjour ${ctx.parentName || "{parent}"} 👋\nVotre compte est prêt\u00a0!\n\nAvec Plan Étude, vous proposez de courtes périodes d’étude à ${child(ctx)}. ${
            ctx.child ? "Il accepte" : "Votre enfant accepte"
          }, réalise ses activités et indique ce qu’il a fait.\n\nVous suivez sa progression sans avoir à le relancer.`,
  },
  {
    key: "p1.exampleAfter",
    flowId: 1,
    step: 0,
    label: "Après l'exemple",
    prompt: "Après la carte d'exemple, une phrase courte qui invite à continuer.",
    text: () => "C’est tout : une activité courte, acceptée par votre enfant, et un suivi simple.",
  },
  {
    key: "p1.inviteFirst",
    flowId: 1,
    step: 1,
    label: "Inviter l'enfant",
    prompt:
      "Aucun enfant n'est encore invité (ou son invitation a été annulée). Propose de l'inviter et dis qu'il recevra un courriel pour activer son compte et accéder à son plan d'étude. Vouvoiement.",
    text: (ctx) =>
      `Commençons par inviter ${ctx.child || "votre enfant"}.\n${
        ctx.child ? "Il" : "Il ou elle"
      } recevra un courriel pour activer son compte et accéder à son plan d’étude.`,
  },
  {
    key: "p1.invited",
    flowId: 1,
    step: 1,
    label: "Invitation envoyée",
    prompt: "L'invitation vient de partir. Confirme et précise que le lien reste valide 14 jours.",
    text: (ctx) => `Invitation envoyée à ${child(ctx)}. Le lien reste valide 14 jours.`,
  },
  {
    key: "p1.emailFixed",
    flowId: 1,
    step: 1,
    label: "Courriel corrigé",
    prompt: "Le parent a corrigé le courriel de l'enfant ; l'invitation est repartie.",
    text: (ctx) => `C’est corrigé. L’invitation est repartie à ${ctx.email || child(ctx)}. Le lien reste valide 14 jours.`,
  },
  {
    key: "p1.childPending",
    flowId: 1,
    step: 1,
    label: "Invitation en attente",
    prompt: "L'enfant a reçu son invitation mais ne l'a pas encore activée. Donne la date d'envoi et l'adresse utilisée.",
    text: (ctx) =>
      `L’invitation de ${child(ctx)} est partie${ctx.email ? ` à ${ctx.email}` : ""}${ctx.invitedAt ? ` le ${day(ctx.invitedAt)}` : ""}. Son compte n’est pas encore activé.\n\nRien reçu ? Vérifiez l’adresse : vous pouvez la corriger.`,
  },
  {
    key: "p1.childExpired",
    flowId: 1,
    step: 1,
    label: "Invitation expirée",
    prompt: "L'invitation de l'enfant a expiré. Propose de la renvoyer.",
    text: (ctx) => `L’invitation de ${child(ctx)} a expiré. Renvoyez-la pour qu’il puisse activer son compte.`,
  },
  {
    key: "p1.childActive",
    flowId: 1,
    step: 1,
    label: "Compte activé",
    prompt: "Le compte de l'enfant est activé. Une phrase courte.",
    text: (ctx) => `${child(ctx)} a activé son compte ✓`,
  },
  {
    key: "p1.childDeactivated",
    flowId: 1,
    step: 1,
    label: "Compte désactivé",
    prompt: "Le compte de l'enfant est désactivé. Dis où le réactiver.",
    text: (ctx) => `Le compte de ${child(ctx)} est désactivé. Vous pouvez le réactiver dans Réglages.`,
  },
  {
    key: "p1.resent",
    flowId: 1,
    step: 1,
    label: "Invitation renvoyée",
    prompt: "L'invitation a été renvoyée. Confirme.",
    text: (ctx) => `Invitation renvoyée à ${child(ctx)}. Le nouveau lien reste valide 14 jours.`,
  },
  {
    key: "p1.chooseChild",
    flowId: 1,
    step: 1,
    label: "Choix de l'enfant",
    prompt: "Plusieurs enfants sont associés au compte. Demande pour lequel commencer.",
    text: () => "Plusieurs enfants sont associés à votre compte. Pour lequel commençons-nous\u00a0?",
  },
  {
    key: "p1.firstPlan",
    flowId: 1,
    step: 2,
    label: "Premier engagement",
    prompt: "Propose de préparer un premier plan d'étude. L'enfant pourra l'accepter dès son compte activé.",
    text: (ctx) =>
      `Voulez-vous proposer un premier plan d’étude à ${child(ctx)}\u00a0?${ctx.active ? "\nIl pourra l’accepter dans son compte." : "\nIl pourra l’accepter dès qu’il aura activé son compte."}`,
  },
  {
    key: "p1.firstPlanSoon",
    flowId: 1,
    step: 2,
    label: "Premier engagement (bientôt)",
    prompt: "La proposition d'activité n'est pas encore disponible. Dis-le simplement, sans bouton.",
    text: (ctx) => `Vous pourrez bientôt proposer une première activité à ${child(ctx)}.`,
  },
  {
    key: "p1.follow",
    flowId: 1,
    step: 3,
    label: "Suivi et rappels",
    prompt:
      "Explique ce que le parent pourra suivre. Ne mentionne le bilan du samedi et les textos que s'ils sont activés.",
    text: (ctx) => {
      const lines = [
        `Vous pourrez suivre les activités acceptées et terminées par ${child(ctx)}, ainsi que ses demandes d’aide.`,
      ];
      if (FEATURES.weeklyReport) lines.push("Chaque samedi, vous recevrez un bilan de sa semaine.");
      if (FEATURES.smsAlerts) {
        lines.push("Les alertes importantes pourront aussi vous être envoyées par texto, selon vos préférences.");
      }
      return lines.join("\n");
    },
  },
  {
    key: "p1.home",
    flowId: 1,
    step: 4,
    label: "Accueil habituel",
    prompt: "Accueil habituel : une phrase courte, puis les raccourcis.",
    text: () => "Que souhaitez-vous faire\u00a0?",
  },
  {
    key: "p1.inviteAnother",
    flowId: 1,
    step: 4,
    label: "Inviter un autre enfant",
    prompt: "Le parent a déjà un enfant et veut en inviter un autre. Une phrase, vouvoiement.",
    text: () => "Invitons un autre enfant. Il recevra un courriel pour activer son compte.",
  },
  {
    key: "p1.soon",
    flowId: 1,
    step: 4,
    label: "Activité bientôt",
    prompt: "Le parent veut proposer une activité, pas encore disponible. Dis-le en une phrase.",
    text: (ctx) => `Vous pourrez bientôt proposer une activité à ${child(ctx)} directement ici.`,
  },
  {
    key: "p1.followEmpty",
    flowId: 1,
    step: 4,
    label: "Suivi vide",
    prompt: "Aucune activité n'existe encore. Dis que le suivi s'affichera dès la première, sans rien inventer.",
    text: () => "Le suivi s’affichera ici dès la première activité acceptée. Seules les activités réellement déclarées sont comptées.",
  },
];
